import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, MapPin, Navigation, Clock, Camera, AlertCircle, IndianRupee, Download, Play, Pause, Gauge, RotateCcw } from 'lucide-react';
import { APIProvider, Map, AdvancedMarker, Polyline, InfoWindow } from '@vis.gl/react-google-maps';
import { reverseGeocode } from '../../lib/google';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Haversine distance in meters
const distMeters = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
};

// Detect stops: clusters where points stay within STOP_RADIUS for >= STOP_MIN_MIN minutes
const STOP_RADIUS_M = 50;
const STOP_MIN_MIN = 5;
const detectStops = (path) => {
  if (!path || path.length === 0) return [];
  const stops = [];
  let cluster = null;
  for (const p of path) {
    const point = { lat: p.latitude, lng: p.longitude, t: new Date(p.timestamp).getTime() };
    if (!cluster) {
      cluster = { center: { lat: point.lat, lng: point.lng }, start: point.t, end: point.t, count: 1 };
      continue;
    }
    if (distMeters(cluster.center, point) <= STOP_RADIUS_M) {
      cluster.end = point.t;
      cluster.count += 1;
    } else {
      const minutes = (cluster.end - cluster.start) / 60000;
      if (minutes >= STOP_MIN_MIN) stops.push({ ...cluster, minutes });
      cluster = { center: { lat: point.lat, lng: point.lng }, start: point.t, end: point.t, count: 1 };
    }
  }
  if (cluster) {
    const minutes = (cluster.end - cluster.start) / 60000;
    if (minutes >= STOP_MIN_MIN) stops.push({ ...cluster, minutes });
  }
  return stops;
};

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '-';
const fmtDur = (mins) => {
  if (mins == null) return '-';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function VisitDetailsModal({ visitId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [startAddr, setStartAddr] = useState(null);
  const [endAddr, setEndAddr] = useState(null);
  const [snapping, setSnapping] = useState(false);

  const loadDetails = async () => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/visits/${visitId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || 'Failed to load visit');
    }
    return r.json();
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await loadDetails();
        if (!alive) return;
        setData(d);
        setLoading(false);

        // If this visit hasn't been road-snapped yet, kick off the snap in the
        // background AFTER the modal has rendered. The call to Google takes
        // ~5-15 s — we don't want to block the modal on it.
        if (d?.snap_meta?.needs_snap) {
          setSnapping(true);
          try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/admin/visits/${visitId}/snap-route`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const js = await res.json().catch(() => null);
            if (!alive) return;
            if (js?.success) {
              // Re-fetch the visit — cached snapped_path is now populated.
              try {
                const fresh = await loadDetails();
                if (alive) setData(fresh);
              } catch {}
            } else if (js) {
              setData(prev => prev ? {
                ...prev,
                snap_meta: { ...(prev.snap_meta || {}), error: js.error, source: js.source, needs_snap: false }
              } : prev);
            }
          } finally {
            if (alive) setSnapping(false);
          }
        }
      } catch (e) {
        if (alive) {
          setErr(e.message || 'Failed to load');
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [visitId]);

  const path = useMemo(() => (data?.gps_path || []).map(p => ({ lat: p.latitude, lng: p.longitude })), [data]);
  const stops = useMemo(() => detectStops(data?.gps_path || []), [data]);

  // ---- Route Replay state (admin-only feature) ----
  const rawPings = data?.gps_path || [];
  const [replayIdx, setReplayIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(2); // 1x, 2x, 4x, 8x — affects step interval
  const tickRef = useRef(null);

  // Reset replay when a different visit loads
  useEffect(() => { setReplayIdx(0); setPlaying(false); }, [visitId]);

  // Auto-advance the slider while playing
  useEffect(() => {
    if (!playing || rawPings.length < 2) return;
    const intervalMs = Math.max(60, 600 / playSpeed); // 1x = 600ms/ping, 8x = ~75ms
    tickRef.current = setInterval(() => {
      setReplayIdx(i => {
        if (i >= rawPings.length - 1) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, intervalMs);
    return () => clearInterval(tickRef.current);
  }, [playing, playSpeed, rawPings.length]);

  // Compute live replay metrics (speed, stop status) at current index
  const replayInfo = useMemo(() => {
    if (rawPings.length === 0) return null;
    const safeIdx = Math.min(Math.max(0, replayIdx), rawPings.length - 1);
    const curr = rawPings[safeIdx];
    const prev = safeIdx > 0 ? rawPings[safeIdx - 1] : null;
    let speedKmh = 0;
    if (prev) {
      const a = { lat: prev.latitude, lng: prev.longitude };
      const b = { lat: curr.latitude, lng: curr.longitude };
      const meters = distMeters(a, b);
      const dtSec = Math.max(1, (new Date(curr.timestamp).getTime() - new Date(prev.timestamp).getTime()) / 1000);
      speedKmh = (meters / dtSec) * 3.6;
    }
    // Determine if this index falls inside a detected stop cluster
    const t = new Date(curr.timestamp).getTime();
    const inStop = stops.find(s => t >= s.start && t <= s.end);
    return {
      pos: { lat: curr.latitude, lng: curr.longitude },
      timestamp: curr.timestamp,
      speedKmh,
      inStop: inStop || null,
      idx: safeIdx,
      total: rawPings.length,
      elapsedMin: rawPings[0] ? (t - new Date(rawPings[0].timestamp).getTime()) / 60000 : 0,
    };
  }, [rawPings, replayIdx, stops]);

  // Traveled / remaining polyline split (uses raw path; matches replay marker exactly)
  const traveledPath = useMemo(() => path.slice(0, Math.max(1, replayIdx + 1)), [path, replayIdx]);
  const remainingPath = useMemo(() => path.slice(Math.max(0, replayIdx)), [path, replayIdx]);

  // Road-snapped polyline now comes from the backend (server-side avoids the
  // browser's referer restrictions on the Google API key). We just consume
  // ``data.snapped_path`` and ``data.snap_meta`` straight from the detail call.
  const snappedPath = useMemo(() => {
    const sp = data?.snapped_path;
    if (!Array.isArray(sp) || sp.length < 2) return null;
    return sp.map(p => ({ lat: p.lat, lng: p.lng }));
  }, [data]);
  const snapMeta = data?.snap_meta || null;

  const drawPath = snappedPath && snappedPath.length > 1 ? snappedPath : path;

  // Reverse-geocode start/end once the visit data lands.
  useEffect(() => {
    let alive = true;
    if (data?.start) {
      reverseGeocode(data.start.latitude, data.start.longitude).then(r => alive && setStartAddr(r?.formatted));
    }
    if (data?.end) {
      reverseGeocode(data.end.latitude, data.end.longitude).then(r => alive && setEndAddr(r?.formatted));
    }
    return () => { alive = false; };
  }, [data]);

  const startPos = data?.start ? { lat: data.start.latitude, lng: data.start.longitude } : null;
  const endPos = data?.end ? { lat: data.end.latitude, lng: data.end.longitude } : null;

  // Multi-supplier: build list of {id, name, pos} for any supplier with a location
  const supplierPins = useMemo(() => {
    const list = data?.suppliers || (data?.supplier ? [data.supplier] : []);
    return (list || [])
      .filter(s => s?.location?.latitude && s?.location?.longitude)
      .map(s => ({ id: s.id, name: s.name, pos: { lat: s.location.latitude, lng: s.location.longitude }, address: s.address }));
  }, [data]);

  const center = startPos || supplierPins[0]?.pos || (path[0] || { lat: 11.0572, lng: 77.3400 });

  // Google KMs = distance derived from the road-snapped GPS polyline (true
  // travelled distance, immune to manual odometer entries).
  // Manual KMs = end_odometer - start_odometer (what the staff entered).
  const googleKm = data?.google_distance_km ?? null;
  const manualKm = data?.distance_km ?? null;
  const isOwn = data?.vehicle_type === 'own';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="visit-details-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Visit Details</h2>
            <p className="text-sm text-blue-100">
              {data?.supplier_name || (data?.suppliers || []).map(s => s.name).join(', ') || 'Visit'} • by {data?.staff_name || '—'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full" data-testid="visit-details-close">
            <X size={22} />
          </button>
        </div>

        {/* Download bar */}
        <div className="px-6 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between text-xs">
          <span className="text-blue-900">Print-friendly trip report — Uber-style summary, stops &amp; photos.</span>
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const r = await fetch(`${API_URL}/api/admin/visits/${visitId}/report-pdf`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!r.ok) { alert('Failed to download'); return; }
                const blob = await r.blob();
                const u = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = u;
                a.download = `carloo_trip_${visitId.slice(0, 8)}.pdf`;
                a.click();
                URL.revokeObjectURL(u);
              } catch { alert('Network error'); }
            }}
            className="flex items-center space-x-1 px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
            data-testid="trip-pdf-download"
          >
            <Download size={14} /><span>Download PDF</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <div className="text-center py-12 text-gray-500">Loading…</div>}
          {err && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-2 text-red-700">
              <AlertCircle size={18} className="mt-0.5" />
              <div>{err}</div>
            </div>
          )}

          {!loading && !err && data && (
            <>
              {/* Top metric strip */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Metric icon={<Navigation size={18} />} label="Google KMs" value={googleKm != null ? `${googleKm.toFixed?.(1) ?? googleKm} km` : '—'} tone="blue" />
                <Metric icon={<Navigation size={18} />} label="Manual KMs" value={manualKm != null ? `${manualKm} km` : '—'} tone="indigo" />
                <Metric icon={<Clock size={18} />} label="Duration" value={data.duration_hours ? `${data.duration_hours}h` : '-'} tone="green" />
                <Metric icon={<MapPin size={18} />} label="Stops" value={stops.length} tone="amber" />
                <Metric icon={<IndianRupee size={18} />} label="Claim" value={isOwn ? `₹${data.expense?.amount ?? 0}` : '—'} tone="purple" />
              </div>

              {/* Vehicle / status row */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${isOwn ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                  {isOwn ? 'Own vehicle' : 'Company vehicle'}
                </span>
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                  {data.status?.replaceAll('_', ' ') || '—'}
                </span>
                {data.purpose && <span className="text-xs text-gray-500">Purpose: {data.purpose}</span>}
                {data.start?.vehicle_km != null && data.end?.vehicle_km != null && (
                  <span className="text-xs text-gray-500">Odometer: {data.start.vehicle_km} → {data.end.vehicle_km}</span>
                )}
              </div>

              {/* Address strip */}
              {(startAddr || endAddr) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" data-testid="trip-address-strip">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <p className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">Started from</p>
                    <p className="text-gray-800">{startAddr || `${data.start?.latitude}, ${data.start?.longitude}`}</p>
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                    <p className="text-[10px] uppercase font-semibold text-red-700 mb-1">Ended at</p>
                    <p className="text-gray-800">{endAddr || (data.end ? `${data.end.latitude}, ${data.end.longitude}` : '—')}</p>
                  </div>
                </div>
              )}

              {/* Map */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-600 flex items-center justify-between gap-2 flex-wrap">
                  <span><strong>Route Map</strong> · Green=Start · Red=End · Orange=Stops (with duration) · Purple=Supplier</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400" data-testid="route-map-stats">
                      {path.length} GPS pings
                      {snappedPath && (
                        <span className="ml-1 text-emerald-600 font-medium">· {snappedPath.length} road-snapped ({snapMeta?.source || 'roads'})</span>
                      )}
                      {snapping && !snappedPath && (
                        <span className="ml-1 text-blue-600 font-medium">· snapping to roads…</span>
                      )}
                    </span>
                    {path.length > 1 && (!snappedPath || snapMeta?.error) && (
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/visits/${visitId}/snap-route`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const j = await r.json();
                            if (j.success) {
                              window.location.reload();  // simplest: re-fetch and re-render
                            } else {
                              alert(`Could not snap route:\n${j.error || 'unknown error'}`);
                            }
                          } catch (e) { alert(`Network error: ${e.message}`); }
                        }}
                        className="px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-semibold"
                        data-testid="resnap-btn"
                      >Snap to roads</button>
                    )}
                  </div>
                </div>
                {snapMeta?.error && !snappedPath && (
                  <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900" data-testid="snap-error-banner">
                    <strong>Showing straight lines.</strong> Road-snap unavailable: <span className="font-mono text-[11px]">{snapMeta.error}</span>.
                    <span className="block mt-0.5 text-amber-800">
                      → Enable Roads API or Directions API on your Google Cloud project, plus add them to your API key's allowed list, then click "Snap to roads".
                    </span>
                  </div>
                )}
                <div className="h-[440px] relative">
                  {MAPS_KEY ? (
                    <APIProvider apiKey={MAPS_KEY}>
                      <Map
                        defaultCenter={center}
                        defaultZoom={path.length > 1 ? 13 : 15}
                        mapId="carloo-visit-details-map"
                        gestureHandling="greedy"
                      >
                        {drawPath.length > 1 && (
                          <Polyline
                            path={drawPath}
                            strokeColor={replayIdx > 0 ? '#94a3b8' : '#1e3a8a'}
                            strokeWeight={replayIdx > 0 ? 5 : 7}
                            strokeOpacity={replayIdx > 0 ? 0.55 : 1}
                            geodesic
                          />
                        )}
                        {replayIdx > 0 && traveledPath.length > 1 && (
                          <Polyline
                            path={traveledPath}
                            strokeColor="#1e3a8a"
                            strokeWeight={7}
                            strokeOpacity={1}
                            geodesic
                          />
                        )}
                        {startPos && (
                          <PinMarker color="#10b981" label="S" position={startPos} onClick={() => setActiveMarker({ kind: 'start' })} />
                        )}
                        {stops.map((s, i) => (
                          <StopMarker
                            key={`stop-${s.center?.lat ?? 0}-${s.center?.lng ?? 0}-${i}`}
                            index={i + 1}
                            duration={fmtDur(s.minutes)}
                            position={s.center}
                            onClick={() => setActiveMarker({ kind: 'stop', index: i })}
                          />
                        ))}
                        {endPos && (
                          <PinMarker color="#ef4444" label="E" position={endPos} onClick={() => setActiveMarker({ kind: 'end' })} />
                        )}
                        {supplierPins.map((s, i) => (
                          <SupplierMarker
                            key={s.id || i}
                            position={s.pos}
                            name={s.name}
                            onClick={() => setActiveMarker({ kind: 'supplier', index: i })}
                          />
                        ))}
                        {replayInfo && replayIdx > 0 && (
                          <PlayheadMarker
                            position={replayInfo.pos}
                            speedKmh={replayInfo.speedKmh}
                            inStop={!!replayInfo.inStop}
                          />
                        )}

                        {activeMarker?.kind === 'start' && startPos && (
                          <InfoWindow position={startPos} onCloseClick={() => setActiveMarker(null)}>
                            <div className="text-xs">
                              <strong className="text-emerald-700">Visit Started</strong>
                              <div>{fmtTime(data.start?.time)}</div>
                              <div>Odometer: {data.start?.vehicle_km}</div>
                            </div>
                          </InfoWindow>
                        )}
                        {activeMarker?.kind === 'end' && endPos && (
                          <InfoWindow position={endPos} onCloseClick={() => setActiveMarker(null)}>
                            <div className="text-xs">
                              <strong className="text-red-700">Visit Ended</strong>
                              <div>{fmtTime(data.end?.time)}</div>
                              <div>Odometer: {data.end?.vehicle_km}</div>
                            </div>
                          </InfoWindow>
                        )}
                        {activeMarker?.kind === 'stop' && stops[activeMarker.index] && (
                          <InfoWindow
                            position={stops[activeMarker.index].center}
                            onCloseClick={() => setActiveMarker(null)}
                          >
                            <div className="text-xs">
                              <strong className="text-orange-700">Stop #{activeMarker.index + 1}</strong>
                              <div>Duration: {fmtDur(stops[activeMarker.index].minutes)}</div>
                              <div>{new Date(stops[activeMarker.index].start).toLocaleTimeString()} → {new Date(stops[activeMarker.index].end).toLocaleTimeString()}</div>
                            </div>
                          </InfoWindow>
                        )}
                        {activeMarker?.kind === 'supplier' && supplierPins[activeMarker.index] && (
                          <InfoWindow position={supplierPins[activeMarker.index].pos} onCloseClick={() => setActiveMarker(null)}>
                            <div className="text-xs">
                              <strong className="text-purple-700">{supplierPins[activeMarker.index].name}</strong>
                              <div className="text-gray-600 max-w-[220px]">{supplierPins[activeMarker.index].address}</div>
                            </div>
                          </InfoWindow>
                        )}
                      </Map>
                    </APIProvider>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-500 text-sm">Maps API key missing.</div>
                  )}
                </div>
              </div>

              {/* Route Replay (Admin) */}
              {rawPings.length > 1 && (
                <RouteReplayPanel
                  info={replayInfo}
                  total={rawPings.length}
                  idx={replayIdx}
                  onScrub={setReplayIdx}
                  playing={playing}
                  onTogglePlay={() => {
                    if (replayIdx >= rawPings.length - 1) setReplayIdx(0);
                    setPlaying(p => !p);
                  }}
                  speed={playSpeed}
                  onSpeed={setPlaySpeed}
                  onReset={() => { setReplayIdx(0); setPlaying(false); }}
                />
              )}

              {/* Stops timeline */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center"><Clock size={16} className="mr-2 text-amber-600" />Stops Timeline ({stops.length})</h3>
                {stops.length === 0 && <p className="text-sm text-gray-500">No stops detected (radius {STOP_RADIUS_M}m, ≥{STOP_MIN_MIN} min).</p>}
                <ol className="space-y-2 text-sm">
                  {stops.map((s, i) => (
                    <li key={`stoprow-${s.start || ''}-${i}`} className="flex items-center justify-between border-b border-gray-100 pb-1 last:border-0">
                      <span className="text-gray-700">#{i + 1} · {new Date(s.start).toLocaleTimeString('en-IN')} → {new Date(s.end).toLocaleTimeString('en-IN')}</span>
                      <span className="font-medium text-orange-700">{fmtDur(s.minutes)}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Photos */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center"><Camera size={16} className="mr-2 text-blue-600" />Photos</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <PhotoTile label="Start KM" photo={data.start?.photo_base64} when={data.start?.time} onOpen={setPreviewPhoto} />
                  <PhotoTile label="End KM" photo={data.end?.photo_base64} when={data.end?.time} onOpen={setPreviewPhoto} />
                </div>
                {data.end?.notes && (
                  <p className="mt-3 text-xs text-gray-600">📝 End notes: {data.end.notes}</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="bg-gray-50 px-5 py-3 border-t flex justify-end">
          <button onClick={onClose} className="px-5 py-2 bg-gray-700 text-white rounded-lg text-sm">Close</button>
        </div>
      </div>

      {previewPhoto && (
        <div className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4" onClick={() => setPreviewPhoto(null)}>
          <button className="absolute top-4 right-4 text-white p-2"><X size={28} /></button>
          <img src={previewPhoto} alt="" className="max-h-[90vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}

// ---- subcomponents ----

const TONE_BG = {
  blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-900',
  indigo: 'from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-900',
  green: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900',
  amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-900',
  purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-900'
};
function Metric({ icon, label, value, tone = 'blue' }) {
  return (
    <div className={`p-3 rounded-xl border bg-gradient-to-br ${TONE_BG[tone] || TONE_BG.blue}`}>
      <div className="flex items-center space-x-2">
        <div className="opacity-70">{icon}</div>
        <p className="text-[10px] uppercase tracking-wide font-semibold">{label}</p>
      </div>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

function PinMarker({ color, label, position, onClick }) {
  return (
    <AdvancedMarker position={position} onClick={onClick}>
      <div
        style={{
          width: 32, height: 32, backgroundColor: color, borderRadius: '50%',
          border: '3px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          color: 'white', fontWeight: 700, fontSize: 13,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
        }}
      >{label}</div>
    </AdvancedMarker>
  );
}

function StopMarker({ index, duration, position, onClick }) {
  return (
    <AdvancedMarker position={position} onClick={onClick}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
        <div
          style={{
            width: 32, height: 32, backgroundColor: '#f97316', borderRadius: '50%',
            border: '3px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            color: 'white', fontWeight: 700, fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >{index}</div>
        <div
          style={{
            marginTop: 2, padding: '2px 6px', backgroundColor: 'white',
            border: '1px solid #f97316', borderRadius: 4, fontSize: 11,
            fontWeight: 700, color: '#9a3412', whiteSpace: 'nowrap',
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
          }}
        >{duration}</div>
      </div>
    </AdvancedMarker>
  );
}

function SupplierMarker({ position, name, onClick }) {
  return (
    <AdvancedMarker position={position} onClick={onClick}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
        <div
          style={{
            width: 34, height: 34, backgroundColor: '#8b5cf6', borderRadius: '50%',
            border: '3px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            color: 'white', fontSize: 16, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >★</div>
        <div
          style={{
            marginTop: 2, padding: '2px 8px', backgroundColor: '#8b5cf6',
            color: 'white', borderRadius: 4, fontSize: 11, fontWeight: 700,
            maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
        >{name}</div>
      </div>
    </AdvancedMarker>
  );
}

function PhotoTile({ label, photo, when, onOpen }) {
  if (!photo) {
    return (
      <div className="aspect-square rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400">
        {label} (none)
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(photo)}
      className="text-left rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 group"
      data-testid={`photo-tile-${label.toLowerCase().replaceAll(' ', '-')}`}
    >
      <img src={photo} alt={label} className="w-full aspect-square object-cover group-hover:opacity-90" />
      <div className="px-2 py-1.5 bg-gray-50">
        <p className="text-xs font-medium text-gray-800">{label}</p>
        {when && <p className="text-[10px] text-gray-500">{fmtTime(when)}</p>}
      </div>
    </button>
  );
}


function PlayheadMarker({ position, speedKmh, inStop }) {
  // Pulsing dot showing the staff's location at the current replay timestamp.
  const color = inStop ? '#f97316' : '#0ea5e9';
  return (
    <AdvancedMarker position={position} zIndex={9999}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            position: 'relative',
            width: 22, height: 22, borderRadius: '50%',
            backgroundColor: color,
            border: '4px solid white',
            boxShadow: `0 0 0 4px ${color}55, 0 2px 8px rgba(0,0,0,0.35)`,
            animation: 'carlooReplayPulse 1.4s ease-out infinite'
          }}
        />
        <div
          style={{
            marginTop: 4, padding: '2px 8px', backgroundColor: 'rgba(15,23,42,0.92)',
            color: 'white', borderRadius: 6, fontSize: 11, fontWeight: 700,
            whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
          }}
        >
          {inStop ? 'Stopped' : `${speedKmh.toFixed(0)} km/h`}
        </div>
      </div>
      <style>{`@keyframes carlooReplayPulse {
        0% { box-shadow: 0 0 0 0 ${color}99, 0 2px 8px rgba(0,0,0,0.35); }
        70% { box-shadow: 0 0 0 14px ${color}00, 0 2px 8px rgba(0,0,0,0.35); }
        100% { box-shadow: 0 0 0 0 ${color}00, 0 2px 8px rgba(0,0,0,0.35); }
      }`}</style>
    </AdvancedMarker>
  );
}

const SPEED_OPTIONS = [1, 2, 4, 8];

function RouteReplayPanel({ info, total, idx, onScrub, playing, onTogglePlay, speed, onSpeed, onReset }) {
  if (!info) return null;
  const t = info.timestamp ? new Date(info.timestamp) : null;
  const speedKmh = info.speedKmh || 0;
  const elapsedH = Math.floor(info.elapsedMin / 60);
  const elapsedM = Math.round(info.elapsedMin % 60);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="route-replay-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center">
          <Gauge size={16} className="mr-2 text-sky-600" />
          Route Replay
          <span className="ml-2 text-[10px] font-medium uppercase tracking-wide bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">Admin</span>
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onTogglePlay}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${playing ? 'bg-amber-500 hover:bg-amber-600' : 'bg-sky-600 hover:bg-sky-700'} text-white`}
            data-testid="replay-play-toggle"
          >
            {playing ? <Pause size={13} /> : <Play size={13} />}
            {playing ? 'Pause' : (idx >= total - 1 ? 'Restart' : 'Play')}
          </button>
          <button
            onClick={onReset}
            className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
            title="Reset"
            data-testid="replay-reset"
          >
            <RotateCcw size={13} />
          </button>
          <div className="flex items-center gap-1 ml-1">
            {SPEED_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => onSpeed(s)}
                className={`px-2 py-1 rounded-md text-[11px] font-semibold ${speed === s ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid={`replay-speed-${s}x`}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Slider */}
      <input
        type="range"
        min={0}
        max={Math.max(0, total - 1)}
        value={idx}
        onChange={e => onScrub(parseInt(e.target.value, 10))}
        className="w-full accent-sky-600"
        data-testid="replay-slider"
      />
      <div className="flex justify-between text-[10px] text-gray-500 mt-1">
        <span>Start</span>
        <span>{idx + 1} / {total}</span>
        <span>End</span>
      </div>

      {/* Live metrics row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
        <ReplayChip label="Time" value={t ? t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'} tone="slate" />
        <ReplayChip label="Speed" value={info.inStop ? '0 km/h' : `${speedKmh.toFixed(0)} km/h`} tone={info.inStop ? 'orange' : 'sky'} />
        <ReplayChip label="Status" value={info.inStop ? `Stopped · ${fmtDur(info.inStop.minutes)}` : 'Moving'} tone={info.inStop ? 'orange' : 'emerald'} />
        <ReplayChip label="Elapsed" value={elapsedH > 0 ? `${elapsedH}h ${elapsedM}m` : `${elapsedM}m`} tone="indigo" />
      </div>
    </div>
  );
}

const CHIP_TONES = {
  slate: 'bg-slate-50 border-slate-200 text-slate-800',
  sky: 'bg-sky-50 border-sky-200 text-sky-800',
  orange: 'bg-orange-50 border-orange-200 text-orange-800',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
};
function ReplayChip({ label, value, tone = 'slate' }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${CHIP_TONES[tone] || CHIP_TONES.slate}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      <p className="text-sm font-bold leading-tight">{value}</p>
    </div>
  );
}
